import React from "react";
import { useDAWStore, INSTRUMENT_DEFS } from "../../store/dawStore";
import { PianoKeys as Piano, Waveform, ChartLineUp, Plus } from "@phosphor-icons/react";

// v11.4 — Track Browser cleanup. "Bass (808)" and the entire Drums
// category (Kick/Snare/Hi-Hat/Clap) were removed per user request —
// producers now use the Sampler to load their own drum + bass sounds
// so the picker doesn't push them toward the built-in options.
const CATEGORIES = [
  {
    id: "instruments", label: "Instruments", icon: Piano,
    items: [
      { type: "synth",   instrumentType: "synth",   name: "Galaxy Synth", color: INSTRUMENT_DEFS.synth.color },
      { type: "sampler", instrumentType: "sampler", name: "Sampler",      color: INSTRUMENT_DEFS.sampler.color },
      { type: "audio",   instrumentType: "audio",   name: "Audio Track",  color: INSTRUMENT_DEFS.audio.color },
    ],
  },
  {
    id: "fx", label: "FX", icon: Waveform,
    items: [
      { type: "fx", plugin: "eq",         name: "Equalizer",  gradient: "linear-gradient(90deg,#ef4444,#f59e0b,#22c55e,#06b6d4,#3b82f6,#a855f7)" },
      { type: "fx", plugin: "compressor", name: "Compressor", color: "#4682B4" },
      { type: "fx", plugin: "distortion", name: "Distortion", color: "#007BFF" },
      { type: "fx", plugin: "limiter",    name: "Limiter",    color: "#FF4500" },
      { type: "fx", plugin: "delay",      name: "Delay",      color: "#B7AEEA" },
      { type: "fx", plugin: "reverb",     name: "Reverb",     color: "#D2691E" },
      { type: "fx", plugin: "pumper",     name: "Crow Pumper", color: "#F43F5E" },
      { type: "fx", plugin: "waveview",   name: "Wave View",  gradient: "linear-gradient(90deg,#10b981,#22d3ee,#a855f7,#ec4899)" },
    ],
  },
  {
    id: "automation", label: "Automation", icon: ChartLineUp,
    // v13.0 — Track-level automation is added per-lane via the "A" button
    // on each track header. The old "Master Volume" placeholder was
    // removed since automation now lives inline in the arrangement.
    items: [],
  },
];

export default function TrackSidebar() {
  const { setActivePlugin, activePlugin, activeTab, addTrack } = useDAWStore();

  const handleItemClick = (item) => {
    if (item.type === "fx" && item.plugin) setActivePlugin(item.plugin);
    if (item.type === "synth" || item.type === "sampler") {
      // Adding an instrument from sidebar creates a new track with that type
      // We'll just open the plugin view; drag to assign to track
      if (item.instrumentType === "synth") setActivePlugin("synth");
      if (item.instrumentType === "sampler") setActivePlugin("sampler");
    }
  };

  const handleDragStart = (e, item) => {
    const payload = item.type === "fx"
      ? { kind: "fx", plugin: item.plugin }
      : { kind: "instrument", instrumentType: item.instrumentType, name: item.name, color: item.color };
    e.dataTransfer.setData("application/x-crow-plugin", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="w-[240px] flex-shrink-0 border-r border-[#262626] bg-[#0e0e0e] flex flex-col overflow-hidden" data-testid="track-sidebar">
      <div className="px-4 py-3 border-b border-[#222] flex items-center justify-between">
        <div className="font-display font-bold text-xs uppercase tracking-wider text-white/70">Track Browser</div>
        <button
          onClick={addTrack}
          className="text-white/40 hover:text-white/90 transition"
          title="Add track"
          data-testid="add-track-global"
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.id} className="border-b border-[#1a1a1a]">
              <div className="px-3 py-2 flex items-center gap-2 bg-[#161616]">
                <Icon size={14} weight="bold" className="text-white/60" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/70">{cat.label}</span>
              </div>
              {cat.items.map((item) => (
                <button
                  key={item.name}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-[#1a1a1a] transition text-left cursor-grab active:cursor-grabbing ${
                    activeTab === "plugin" && activePlugin === item.plugin ? "bg-[#1a1a1a]" : ""
                  }`}
                  data-testid={`sidebar-item-${item.instrumentType || item.plugin || item.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{
                      background: item.gradient || item.color,
                      boxShadow: item.color ? `0 0 6px ${item.color}` : undefined,
                    }}
                  />
                  <span className="text-[12px] flex-1 truncate">{item.name}</span>
                  <span className="label-mono opacity-50">⠿</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="px-3 py-2 border-t border-[#262626] bg-[#0a0a0a]">
        <div className="label-mono">Drag any item onto a track header →</div>
      </div>
    </div>
  );
}
